import { FC, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-final-form'
import MockOrderData from 'components/MockData/MockOrderData'
import { required } from 'forms/validators'
import { objectDiff } from 'helpers/diff'
import { useTypedSelector } from 'hooks'
import { debounce } from 'lodash'
import { useGetDeliveryCostMutation } from 'modules/deliveryInfo/api'
import { DeliveryCostRequest, DeliveryTypeEnum } from 'modules/deliveryInfo/types'
import OrderCargoBlockForm from 'modules/order/components/OrderGuestCreateForm/OrderCargoBlockForm'
import { OrderSummaryBlock } from 'modules/order/components/OrderSummaryBlock'
import { FieldOrderCreateContactItem } from 'modules/order/forms'
import { FieldOrderCreateComment } from 'modules/order/forms/FieldOrderCreateComment'
import FieldOrderCreateCustomer from 'modules/order/forms/FieldOrderCreateCustomer'
import { FieldOrderCreateFormDate } from 'modules/order/forms/FieldOrderCreateFormDate'
import { FieldOrderCreateFormRoute } from 'modules/order/forms/FieldOrderCreateFormRoute'
import { FieldOrderCreatePayer } from 'modules/order/forms/FieldOrderCreatePayer'
import { prepareCargo, prepareCargoTotal, prepareOperationsForDeliveryInfo } from 'modules/order/helpers/order'
import { TOrderCreateFormData } from 'modules/order/types/orderCreateTypes'
import { TOrderGuestCreateCargo, TOrderGuestCreateCargoTotal } from 'modules/order/types/orderGuestCreateTypes'
import { TScheduleDay } from 'modules/terminal/types'
import { CourierDeliveryInfo, OrderCreateRequest, OrderItemClient, UserInfo } from 'schema'
import { CustomerAddLocationKeys } from 'store/customerAdd/constants'
import { orderOperation } from 'utils/feature'

import { ModalUserInfo } from '../../components/ModalUserInfo/ModalUserInfo'
import { OrderOperationsBlock } from '../../components/OrderOperationsBlock/OrderOperationsBlock'
import { FieldOrderCreateCommonOperation } from '../../forms/FieldOrderCreateCommonOperation'
import { FieldOrderCreatePointOperation } from '../../forms/FieldOrderCreatePointOperation'
import { DEBOUNCE_DELAY } from './constants'

// TODO вынести в декораторы формы
export function validateCourierPoint(point: {
  addressDetail: {
    fiasId: string | undefined
    latitude: string | undefined
    longitude: string | undefined
  }
  isCourier: boolean | undefined
  terminalId: string | undefined
}) {
  const { addressDetail } = point

  return Boolean(
    point.isCourier &&
      addressDetail &&
      addressDetail.latitude &&
      addressDetail.longitude &&
      point.terminalId &&
      addressDetail.fiasId
  )
}
// TODO вынести в декораторы формы
export function validateTerminalPoint(point: { isCourier: boolean | undefined; terminalId: string | undefined }) {
  return Boolean(!point.isCourier && point.terminalId)
}
// TODO вынести в декораторы формы
export function validateCargo(cargos: TOrderGuestCreateCargo[]) {
  return cargos.every((cargo) => cargo.type && cargo.height && cargo.width && cargo.length && cargo.weight)
}
// TODO вынести в декораторы формы
export function validateCargoTotal(cargoTotal: TOrderGuestCreateCargoTotal | undefined) {
  if (!cargoTotal) {
    return false
  }

  return !(
    !cargoTotal.type ||
    !cargoTotal.weight ||
    !cargoTotal.volume ||
    !cargoTotal.height ||
    !cargoTotal.width ||
    !cargoTotal.length ||
    !cargoTotal.amount
  )
}

interface OrderCreateFormProps {
  isCompleteUserDocument: boolean
  isCompleteUserName: boolean
  isShowUserInfoError: boolean
  user: UserInfo | undefined
}

const OrderCreateForm: FC<OrderCreateFormProps> = ({
  user,
  isShowUserInfoError,
  isCompleteUserName,
  isCompleteUserDocument
}) => {
  const form = useForm<TOrderCreateFormData>()
  const { values } = form.getState()
  const { customer, cargo, cargoTotal, cargoType } = values
  const userContact = useMemo(
    () => ({
      firstname: user?.firstname || '',
      lastname: user?.lastname || '',
      secondname: user?.secondname,
      phone: user?.phone ? user?.phone : '',
      phoneExt: ''
    }),
    [user?.firstname, user?.lastname, user?.phone, user?.secondname]
  )
  const [, setIsDeparturePartnerUpdate] = useState<boolean>(false)
  const [, setIsDestinationPartnerUpdate] = useState<boolean>(false)

  const customerTypeValue = useMemo(() => customer?.type || OrderItemClient.type.LEGAL, [customer?.type])
  const [errorDepartureAddress, setErrorDepartureAddress] = useState('')
  const [errorDestinationAddress, setErrorDestinationAddress] = useState('')
  const [searchDepartureAddressValue, setSearchDepartureAddressValue] = useState('')
  const [searchDestinationAddressValue, setSearchDestinationAddressValue] = useState('')
  const [lastDataForCalc, setLastDataForCalc] = useState<any[]>([])

  const [departureTerminalId, setDepartureTerminalId] = useState('')
  const [destinationTerminalId, setDestinationTerminalId] = useState('')
  const [departureType, setDepartureType] = useState<DeliveryTypeEnum | undefined>()
  const [destinationType, setDestinationType] = useState<DeliveryTypeEnum | undefined>()
  const [departureTerminalSchedule, setDepartureTerminalSchedule] = useState<TScheduleDay[] | undefined>()
  const [destinationTerminalSchedule, setDestinationTerminalSchedule] = useState<TScheduleDay[] | undefined>()

  const [, setIsContactsByCustomerList] = useState(false)

  const [getDeliveryCost, { data: deliveryCost, isLoading: isDeliveryCostLoading, reset: resetDeliveryCost }] =
    useGetDeliveryCostMutation()

  const debouncedGetDeliveryCost = useMemo(() => debounce(getDeliveryCost, DEBOUNCE_DELAY), [getDeliveryCost])

  const { fromPoint, toPoint, customerId } = useTypedSelector((state) => state.orderAddReducer)

  const fromRes = useMemo(() => {
    const rawRes = {
      terminalId: fromPoint.selectedTerminal?.id,
      isCourier: fromPoint.isCourier,
      addressDetail: {
        latitude: fromPoint.isCourier ? fromPoint.selectedOption?.latitude : fromPoint.selectedTerminal?.latitude,
        longitude: fromPoint.isCourier ? fromPoint.selectedOption?.longitude : fromPoint.selectedTerminal?.longitude,
        fiasId: fromPoint.selectedOption?.fiasId
      }
    }
    if (rawRes.isCourier) {
      return rawRes
    }

    return {
      terminalId: rawRes.terminalId,
      isCourier: rawRes.isCourier
    }
  }, [fromPoint.selectedTerminal, fromPoint.isCourier, fromPoint.selectedOption])

  const fromResValid = useMemo(() => {
    if (fromPoint.errorAddress) {
      return false
    }
    if (fromRes.isCourier) {
      return validateCourierPoint(fromRes)
    }

    return validateTerminalPoint(fromRes)
  }, [fromRes, fromPoint])

  const toRes = useMemo(() => {
    const rawRes = {
      terminalId: toPoint.selectedTerminal?.id,
      isCourier: toPoint.isCourier,
      addressDetail: {
        latitude: toPoint.isCourier ? toPoint.selectedOption?.latitude : toPoint.selectedTerminal?.latitude,
        longitude: toPoint.isCourier ? toPoint.selectedOption?.longitude : toPoint.selectedTerminal?.longitude,
        fiasId: toPoint.selectedOption?.fiasId
      }
    }
    if (rawRes.isCourier) {
      return rawRes
    }

    return {
      terminalId: rawRes.terminalId,
      isCourier: rawRes.isCourier
    }
  }, [toPoint])

  const toResValid = useMemo(() => {
    if (toPoint.errorAddress) {
      return false
    }
    if (toRes.isCourier) {
      return validateCourierPoint(toRes)
    }

    return validateTerminalPoint(toRes)
  }, [toRes, toPoint])

  const cargoRes = useMemo(() => prepareCargo(cargo), [cargo])

  const cargoResValid = useMemo(() => (cargo ? validateCargo(cargoRes) : false), [cargoRes, cargo])

  const cargoTotalRes = useMemo(() => prepareCargoTotal(cargoTotal), [cargoTotal])

  const cargoTotalResValid = useMemo(
    () => (cargoTotal ? validateCargoTotal(cargoTotalRes) : false),
    [cargoTotalRes, cargoTotal]
  )

  const cargoFullRes = useMemo(() => {
    if (cargoType === OrderCreateRequest.cargoType.TOTAL) {
      return {
        cargoInfo: {
          type: cargoType,
          total: {
            type: cargoTotalRes?.type,
            weight: cargoTotalRes?.weight,
            volume: cargoTotalRes?.volume,
            amount: cargoTotalRes?.amount
          }
        }
      }
    }

    return {
      cargoInfo: {
        type: cargoType,
        separate: cargoRes.map((item) => ({
          amount: item.amount,
          length: item.length,
          width: item.width,
          type: item.type,
          height: item.height,
          weight: item.weight
        }))
      }
    }
  }, [cargoRes, cargoTotalRes, cargoType])

  const cargoFullResValid = useMemo(() => {
    if (cargoType === OrderCreateRequest.cargoType.TOTAL) {
      return cargoTotalResValid
    }
    if (cargoType === OrderCreateRequest.cargoType.SEPARATE) {
      return cargoResValid
    }
  }, [cargoResValid, cargoTotalResValid, cargoType])

  const preparedOperations = useMemo(
    () => prepareOperationsForDeliveryInfo(values.operation || [], departureTerminalId, destinationTerminalId),
    [values.operation, departureTerminalId, destinationTerminalId]
  )

  useEffect(() => {
    if (
      !Object.keys(
        objectDiff(
          [
            { customerId: customer?.customerId },
            toRes,
            fromRes,
            toResValid,
            fromResValid,
            debouncedGetDeliveryCost,
            cargoTotalResValid,
            cargoType,
            cargoFullResValid,
            cargoFullRes,
            preparedOperations
          ],
          lastDataForCalc
        )
      ).length
    ) {
      return
    }

    setLastDataForCalc([
      { customerId: customer?.customerId },
      toRes,
      fromRes,
      toResValid,
      fromResValid,
      debouncedGetDeliveryCost,
      cargoTotalResValid,
      cargoType,
      cargoFullResValid,
      cargoFullRes,
      preparedOperations
    ])

    if (!cargoType || !fromResValid || !toResValid || !cargoFullResValid) {
      resetDeliveryCost()

      return
    }

    debouncedGetDeliveryCost({
      customerId: customer?.customerId,
      ...preparedOperations,
      departure: { ...preparedOperations.departure, point: fromRes },
      destination: { ...preparedOperations.destination, point: toRes },
      ...cargoFullRes
    } as unknown as DeliveryCostRequest)
    // eslint-disable-next-line
  }, [
    toRes,
    fromRes,
    toResValid,
    fromResValid,
    debouncedGetDeliveryCost,
    cargoTotalResValid,
    cargoType,
    cargoFullResValid,
    cargoFullRes,
    preparedOperations
  ])

  return (
    <>
      <div className="relative grid grid-cols-1 gap-x-6 gap-y-2.5 min-[701px]:grid-cols-[3fr_2fr]">
        <div className="mb-12 min-[701px]:mb-0">
          {isShowUserInfoError && (
            <ModalUserInfo isCompleteUserDocument={isCompleteUserDocument} isCompleteUserName={isCompleteUserName} />
          )}

          <FieldOrderCreateCustomer
            customerTypeValue={customerTypeValue}
            name="customer"
            setIsContactsByCustomerList={setIsContactsByCustomerList}
            validate={required}
          />
          <FieldOrderCreateFormRoute
            className="mt-6 md:mt-8"
            customerId={customerId}
            customerTypeValue={customerTypeValue}
            errorAddress={errorDepartureAddress}
            name="departurePoint"
            searchAddressValue={searchDepartureAddressValue}
            setDepartureTerminalSchedule={setDepartureTerminalSchedule}
            setErrorAddress={setErrorDepartureAddress}
            setPointType={setDepartureType}
            setSearchAddressValue={setSearchDepartureAddressValue}
            setTerminalId={setDepartureTerminalId}
            type={CourierDeliveryInfo.pointType.DEPARTURE}
            validate={required}
          />
          {orderOperation && !fromPoint.isCourier && (
            <FieldOrderCreatePointOperation name="operation" pointType="departure" terminalId={departureTerminalId} />
          )}
          <FieldOrderCreateFormRoute
            className="mt-6 md:mt-8"
            customerId={customerId}
            customerTypeValue={customerTypeValue}
            departureTerminalId={departureTerminalId}
            errorAddress={errorDestinationAddress}
            name="destinationPoint"
            searchAddressValue={searchDestinationAddressValue}
            setDestinationTerminalSchedule={setDestinationTerminalSchedule}
            setErrorAddress={setErrorDestinationAddress}
            setPointType={setDestinationType}
            setSearchAddressValue={setSearchDestinationAddressValue}
            setTerminalId={setDestinationTerminalId}
            type={CourierDeliveryInfo.pointType.DESTINATION}
            validate={required}
          />
          {orderOperation && !toPoint.isCourier && (
            <FieldOrderCreatePointOperation
              name="operation"
              pointType="destination"
              terminalId={destinationTerminalId}
            />
          )}
          <FieldOrderCreateFormDate
            className="mt-6 md:mt-8"
            departureTerminalId={departureTerminalId}
            departureTerminalSchedule={departureTerminalSchedule}
            departureType={departureType}
            destinationTerminalId={destinationTerminalId}
            destinationTerminalSchedule={destinationTerminalSchedule}
            destinationType={destinationType}
            name="departureDate"
            validate={required}
            departureSelectedOption={{
              latitude: fromPoint?.selectedOption?.latitude,
              longitude: fromPoint?.selectedOption?.longitude
            }}
            destinationSelectedOption={{
              latitude: toPoint?.selectedOption?.latitude,
              longitude: toPoint?.selectedOption?.longitude
            }}
          />
          <OrderCargoBlockForm className="mt-6 md:mt-8" />
          <FieldOrderCreateContactItem
            className="mt-6 md:mt-8"
            customerTypeValue={customerTypeValue}
            name="departurePartner"
            setIsPartnerUpdate={setIsDeparturePartnerUpdate}
            storeKey={CustomerAddLocationKeys.OrderAddDeparturePartner}
            type={CourierDeliveryInfo.pointType.DEPARTURE}
            userContact={userContact}
          />
          <FieldOrderCreateContactItem
            className="mt-6 md:mt-8"
            customerTypeValue={customerTypeValue}
            name="destinationPartner"
            setIsPartnerUpdate={setIsDestinationPartnerUpdate}
            storeKey={CustomerAddLocationKeys.OrderAddDestinationPartner}
            type={CourierDeliveryInfo.pointType.DESTINATION}
            userContact={userContact}
          />
          <FieldOrderCreatePayer className="mt-6 md:mt-8" customerTypeValue={customerTypeValue} name="payer" />
          <h2 className="mb-2 mt-6 text-headline-sm font-normal text-on-surface md:mt-8">Прочее</h2>
          {orderOperation && <FieldOrderCreateCommonOperation className="pb-3 pt-2" name="operation" />}
          <FieldOrderCreateComment name="comment" />
        </div>
        <div className="min-[701px]:pl-6">
          <div className="grid grid-cols-1 content-start gap-y-10 min-[701px]:sticky min-[701px]:top-[90px]">
            <OrderSummaryBlock
              cargoInfo={{
                // TODO исправить
                // @ts-ignore
                separate: prepareCargo(values.cargo),
                // TODO исправить
                // @ts-ignore
                total: prepareCargoTotal(values.cargoTotal),
                // TODO исправить
                // @ts-ignore
                type: values.cargoType
              }}
            />
            <OrderOperationsBlock
              className="mb-14 md:mb-0"
              departureTerminal={fromPoint.selectedTerminal}
              destinationTerminal={toPoint.selectedTerminal}
              isLoading={isDeliveryCostLoading}
              operations={deliveryCost?.operation || []}
              orderOperation={values.operation}
              totalPrice={deliveryCost?.totalPrice}
            />
          </div>
        </div>
      </div>
      <MockOrderData />
    </>
  )
}

export default OrderCreateForm
